<template>
  <div class="common-card">
    <modal-bank-record-filter
      :filter_payment_method_id.sync="filter_payment_method_id"
      :filter_start_date.sync="filter_start_date"
      :filter_end_date.sync="filter_end_date"
      :filter_min_amount.sync="filter_min_amount"
      :filter_max_amount.sync="filter_max_amount"
      :filter_is_savings.sync="filter_is_savings"
      :filter_transaction_type.sync="filter_transaction_type"

      :paymentMethodOptions="paymentMethodOptions"
      @refetch-data="refetchData"
    />
    <modal-add-misc-record
      :paymentMethodOptions="paymentMethodOptions"
      @refetch-data="refetchData"
      @success="showToast"
      @error="showToast"
      @hide-modal="$bvModal.hide('modal-add-misc-record')"
    />
    <b-card class="top mb-0">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex mb-1 mb-sm-0 align-items-center">
          <h4 class="font-weight-bolder ml-1 text-nowrap mb-0">
            <font-awesome-icon
              class="mr-50"
              icon="fa-solid fa-university"
            />
            銀行進支記錄
          </h4>
          <div class="d-flex ml-3 align-items-center">
            <label>顯示</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>條目</label>
          </div>
        </div>
        <div class="d-flex flex-wrap mobile-w100 mt-1 mt-lg-0">
          <b-form-input
            v-model="searchQuery"
            class="search-form mb-1 mb-sm-0 mr-sm-1"
            placeholder="搜尋銀行進支記錄"
          />
          <b-button
            v-if="ability.can('create', 'bank_records')"
            variant="success"
            class="mobile-w100 mb-1 mb-sm-0 mr-sm-1"
            @click="$bvModal.show('modal-add-misc-record')"
          >
            <feather-icon
              class="mr-25"
              icon="PlusIcon"
              size="18"
            />
            新增記錄
          </b-button>
          <b-button
            variant="primary"
            class="mobile-w100 mb-1 mb-sm-0 mr-sm-1"
            @click="$bvModal.show('modal-bank-record-filter')"
            ><feather-icon
              class="mr-1"
              icon="filterIcon"
              size="18"
            ></feather-icon
            >篩選</b-button
          >
          <!-- 匯出功能按鈕 -->
          <b-dropdown
            v-if="ability.can('export', 'bank_records')"
            variant="success"
            text="匯出"
            class="mobile-w100 mb-1 mb-sm-0"
            :disabled="isExporting"
          >
            <template #button-content>
              <b-spinner
                v-if="isExporting"
                small
                class="mr-1"
              ></b-spinner>
              <feather-icon
                v-else
                size="16"
                class="mr-1"
                icon="DownloadIcon"
              />
              {{ isExporting ? '匯出中...' : '匯出' }}
            </template>
            <b-dropdown-item
              @click="exportCurrentPage"
              :disabled="isExporting"
            >
              <feather-icon size="16" class="mr-1" icon="FileTextIcon" />
              匯出此頁面
            </b-dropdown-item>
            <b-dropdown-item
              @click="exportAllRecords"
              :disabled="isExporting"
            >
              <feather-icon size="16" class="mr-1" icon="DatabaseIcon" />
              匯出全部記錄
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </b-card>

    <b-card no-body class="card-statistics bottom">
      <b-card-body class="p-2">
        <b-table
          ref="refBankRecordsListTable"
          class="position-relative bank-records-table"
          :items="fetchBankRecords"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="未找到匹配的記錄"
          :sort-desc.sync="isSortDirDesc"
        >
          <!-- Column: date 日期 -->
          <template #cell(date)="data">
            <span class="font-weight-medium">
              {{ formatDate(data.item.date) }}
            </span>
          </template>

          <!-- Column: transaction_detail 進支詳情 -->
          <template #cell(transaction_detail)="data">
            <div class="transaction-detail">
              <span class="detail-text" v-html="formatTransactionDetail(data.item)"></span>
              <!-- <small class="text-muted d-block mt-1">
                {{ getRecordTypeLabel(data.item.record_type) }}
              </small> -->
            </div>
          </template>

          <!-- Column: payment_method 進支方式 -->
          <template #cell(payment_method)="data">
            <div class="payment-method">
              <span
                v-for="(line, index) in data.item.payment_method.split('\n')"
                :key="index"
                :class="{ 'text-muted': index > 0, 'small': index > 0 }"
              >
                {{ line }}
                <br v-if="index < data.item.payment_method.split('\n').length - 1" />
              </span>
            </div>
          </template>

          <!-- Column: deposit_amount 存入 -->
          <template #cell(deposit_amount)="data">
            <span
              v-if="data.item.deposit_amount > 0"
              class="text-success font-weight-medium d-block"
            >
              HK${{ Number(data.item.deposit_amount).toFixed(2) }}
            </span>
          </template>

          <!-- Column: withdrawal_amount 支出 -->
          <template #cell(withdrawal_amount)="data">
            <span
              v-if="data.item.withdrawal_amount > 0"
              class="text-danger font-weight-medium d-block"
            >
              HK${{ Number(data.item.withdrawal_amount).toFixed(2) }}
            </span>
          </template>

          <!-- Column: is_savings 儲蓄/往來 -->
          <template #cell(is_savings)="data">
            <b-form-checkbox
              v-model="data.item.is_savings"
              switch
              inline
              @change="updateSavingsStatus(data.item)"
              class="savings-switch"
            >
              <span class="switch-label">
                {{ data.item.is_savings ? '儲蓄' : '往來' }}
              </span>
            </b-form-checkbox>
          </template>

          <!-- Column: remark 備注 -->
          <template #cell(remark)="data">
            <div class="remark-field" v-if="ability.can('remark_update', 'bank_records')">
              <b-form-input
                v-model="data.item.remark"
                placeholder="輸入備注..."
                size="sm"
                @blur="updateRemark(data.item)"
                class="remark-input"
              />
            </div>
          </template>

          <!-- Column: actions 刪除 -->
          <template #cell(actions)="data">
            <div v-if="data.item.record_type === 'misc_bank_record' && ability.can('delete', 'bank_records')" class="text-center">
              <b-button
                variant="outline-danger"
                size="sm"
                @click="deleteMiscRecord(data.item)"
                v-b-tooltip.hover
                title="刪除雜項記錄"
              >
                <feather-icon icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">顯示 {{ dataMeta.from }} 到 {{ dataMeta.to }} 筆，共 {{ dataMeta.of }} 筆記錄</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRow"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>
    
<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BButton,
  BTable,
  BPagination,
  BBadge,
  BDropdown,
  BDropdownItem,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import useBankRecordsList from "./useBankRecordsList";
import bankRecordsStoreModule from "../bankRecordsStoreModule";
import ModalBankRecordFilter from "../modal/ModalBankRecordFilter.vue";
import ModalAddMiscRecord from "../modal/ModalAddMiscRecord.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      isExporting: false, // 匯出狀態
    };
  },
  mounted() {
    // 添加點擊事件監聽器處理編號點擊
    this.$nextTick(() => {
      document.addEventListener('click', this.handleRecordClick);
    });
  },
  beforeDestroy() {
    // 移除事件監聽器
    document.removeEventListener('click', this.handleRecordClick);
  },
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BButton,
    BTable,
    BPagination,
    BBadge,
    BDropdown,
    BDropdownItem,
    BSpinner,
    vSelect,
    ModalBankRecordFilter,
    ModalAddMiscRecord,
  },
  methods: {
    // 獲取記錄類型標籤
    getRecordTypeLabel(recordType) {
      const typeLabels = {
        'receipt_item': '收據項目',
        'supplier_document': '供應商文件',
        'claim': '報銷記錄'
      };
      return typeLabels[recordType] || '未知類型';
    },

    // 更新儲蓄/往來狀態
    updateSavingsStatus(item) {
      this.$store
        .dispatch("bankRecords/updateSavingsStatus", {
          record_type: item.record_type,
          record_id: item.record_id,
          is_savings: item.is_savings
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "更新成功",
              text: `已將記錄設為${item.is_savings ? '儲蓄' : '往來'}`,
              icon: "CheckCircleIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          console.error('更新儲蓄/往來狀態失敗:', error);
          // 回復原狀態
          item.is_savings = !item.is_savings;
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "更新失敗",
              text: "儲蓄/往來狀態更新失敗，請稍後再試",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    // 更新備注
    updateRemark(item) {
      this.$store
        .dispatch("bankRecords/updateRemark", {
          record_type: item.record_type,
          record_id: item.record_id,
          remark: item.remark || ''
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "備注已更新",
              text: "備注內容已成功儲存",
              icon: "CheckCircleIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          console.error('更新備注失敗:', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "更新失敗",
              text: "備注更新失敗，請稍後再試",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    // 刪除雜項記錄
    deleteMiscRecord(item) {
      this.$bvModal.msgBoxConfirm(`確定要刪除此雜項記錄嗎？`, {
        title: '確認刪除',
        okVariant: 'danger',
        okTitle: '刪除',
        cancelTitle: '取消',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      })
        .then(async (confirm) => {
          if (confirm) {
            try {
              await this.$store.dispatch("bankRecords/deleteMiscRecord", {
                record_id: item.record_id,
              });
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "刪除成功",
                  text: "雜項記錄已成功刪除",
                  icon: "CheckCircleIcon",
                  variant: "success",
                },
              });
              this.refetchData(); // 重新獲取數據
            } catch (error) {
              console.error('刪除雜項記錄失敗:', error);
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "刪除失敗",
                  text: "刪除雜項記錄失敗，請稍後再試",
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            }
          }
        })
        .catch(err => {
          // User cancelled
          if (err.name === 'Cancel') {
            return;
          }
        });
    },

    // 顯示 Toast 提示
    showToast(toastConfig) {
      this.$toast(toastConfig);
    },

    // 獲取收款方式名稱
    getPaymentMethod(paymentMethodId) {
      if (!paymentMethodId || !this.paymentMethodOptions || !this.paymentMethodOptions.length) {
        return '-';
      }
      const method = this.paymentMethodOptions.find(m => m.id === paymentMethodId);
      return method ? method.name : '-';
    },

    // 格式化日期 - 統一為 YYYY-MM-DD 格式
    formatDate(date) {
      if (!date) return '-';
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '-';

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    // 格式化交易詳情，將編號設為可點擊連結
    formatTransactionDetail(item) {
      const detail = item.transaction_detail || '';
      const recordType = item.record_type;

      // 根據記錄類型獲取正確的 ID
      let targetId = item.record_id; // 預設使用 record_id

      if (recordType === 'receipt_item' && item.original_data && item.original_data.receipt) {
        // 收據記錄：使用 original_data.receipt.id
        targetId = item.original_data.receipt.id;
      } else if (recordType === 'supplier_document' && item.original_data) {
        // 供應商記錄：使用 original_data.user_id (供應商 ID)
        targetId = item.original_data.user_id;
      } else if (recordType === 'supplier_payment_proof' && item.original_data && item.original_data.bill) {
        // 供應商付款憑證記錄：使用 original_data.bill.user_id (供應商 ID)
        targetId = item.original_data.bill.user_id;
      } else if ((recordType === 'prepayment_transaction' || recordType === 'prepayment_salary') && item.prepayment_id) {
        // 預支記錄：使用 prepayment_id
        targetId = item.prepayment_id;
      }

      // 檢查預支記錄編輯權限
      const canEditPrepayment = this.ability.can('update', 'prepayments') || this.ability.can('update', 'user_prepayments');

      // 使用正則表達式匹配不同類型的編號
      if (recordType === 'claim' && detail.includes('CL')) {
        // 報銷記錄：CL開頭
        return detail.replace(/(CL\d+)/g, `<a href="#" class="record-link text-primary" data-type="claim" data-id="${targetId}">$1</a>`);
      } else if (recordType === 'supplier_document' && detail.includes('T')) {
        // 供應商記錄：T開頭
        return detail.replace(/(T\d+)/g, `<a href="#" class="record-link text-primary" data-type="supplier" data-id="${targetId}">$1</a>`);
      } else if (recordType === 'supplier_payment_proof' && detail.includes('T')) {
        // 供應商付款憑證記錄：特殊格式處理
        return this.formatSupplierPaymentProofDetail(detail, targetId);
      } else if (recordType === 'receipt_item' && detail.includes('R')) {
        // 收據記錄：R開頭
        return detail.replace(/(R\d+)/g, `<a href="#" class="record-link text-primary" data-type="receipt" data-id="${targetId}">$1</a>`);
      } else if (recordType === 'expense' && detail.includes('薪金支出：')) {
        // 薪金支出記錄：薪金支出：H000012 格式
        return detail.replace(/薪金支出：(H\d+)/g, `薪金支出：<a href="#" class="record-link text-primary" data-type="expense" data-id="${targetId}">$1</a>`);
      } else if ((recordType === 'prepayment_transaction' || recordType === 'prepayment_salary' || recordType === 'prepayment_merged') && item.prepayment_number) {
        // 預支記錄：根據權限決定是否顯示連結
        const prepaymentNumber = item.prepayment_number;
        let formattedDetail = '';

        if (canEditPrepayment) {
          // 有權限：顯示可點擊連結
          formattedDetail = detail.replace(new RegExp(`(${prepaymentNumber})`, 'g'),
            `<a href="#" class="record-link text-primary" data-type="prepayment" data-id="${targetId}">$1</a>`);
        } else {
          // 無權限：顯示純文字
          formattedDetail = detail;
        }

        // 如果是合併記錄且同時包含預支工資和採購，添加提示文字
        if (recordType === 'prepayment_merged' && item.has_both_types) {
          formattedDetail += '<br><span class="text-muted small">此記錄包含預支工資及採購</span>';
        }

        return formattedDetail;
      } else if (recordType === 'refund' && detail.includes('RF')) {
        // 退款記錄：RF開頭
        return detail.replace(/(RF\d+)/g, `<a href="#" class="record-link text-primary" data-type="refund" data-id="${targetId}">$1</a>`);
      }

      return detail;
    },

    // 格式化供應商付款憑證詳情
    formatSupplierPaymentProofDetail(detail, targetId) {
      // 原始格式：供應商支出：T000005 - 專業鎖類中心有限公司\n帳單：91718696  憑證: ven0001
      // 目標格式：
      // 第一行：供應商支出：T000005 - 專業鎖類中心有限公司
      // 第二行：帳單：91718696 憑證: ven0001 (用 text-muted)

      const lines = detail.split('\n');
      if (lines.length >= 2) {
        const supplierLine = lines[0]; // 供應商支出：T000005 - 專業鎖類中心有限公司
        const billLine = lines[1]; // 帳單：91718696  憑證: ven0001

        // 處理供應商行，將 T 編號設為可點擊連結
        const formattedSupplierLine = supplierLine.replace(/(T\d+)/g,
          `<a href="#" class="record-link text-primary" data-type="supplier" data-id="${targetId}">$1</a>`);

        // 處理帳單行，設為 text-muted 樣式
        const formattedBillLine = `<span class="text-muted" style="font-size:12px">${billLine}</span>`;

        return `${formattedSupplierLine}<br>${formattedBillLine}`;
      }

      // 如果格式不符合預期，回退到原始處理方式
      return detail.replace(/(T\d+)/g, `<a href="#" class="record-link text-primary" data-type="supplier" data-id="${targetId}">$1</a>`);
    },

    // 處理編號點擊事件
    handleRecordClick(event) {
      if (event.target.classList.contains('record-link')) {
        event.preventDefault();
        const type = event.target.getAttribute('data-type');
        const id = event.target.getAttribute('data-id');

        this.navigateToRecord(type, id);
      }
    },

    // 導航到對應的記錄頁面
    navigateToRecord(type, id) {
      switch (type) {
        case 'claim':
          // 在新視窗中開啟報銷編輯頁面
          const claimUrl = this.$router.resolve({ name: 'claim-edit', params: { id } }).href;
          window.open(claimUrl, '_blank');
          break;
        case 'supplier':
          // 在新視窗中開啟供應商詳情頁面
          const supplierUrl = this.$router.resolve({ name: 'staff-supplier-view', params: { id } }).href;
          window.open(supplierUrl, '_blank');
          break;
        case 'receipt':
          // 在新視窗中開啟收據編輯頁面
          const receiptUrl = this.$router.resolve({ name: 'receipt-edit', params: { id } }).href;
          window.open(receiptUrl, '_blank');
          break;
        case 'expense':
           // 在新視窗中開啟預支記錄編輯頁面
          // 根據使用者權限決定跳轉到哪個路由
          let expenseUrl;
          if (this.ability.can('update', 'expenditures')) {
            // 管理員權限：跳轉到一般預支編輯頁面
            expenseUrl = this.$router.resolve({ name: 'expenditure-edit', params: { id } }).href;
          } else {
            console.warn('沒有預支記錄編輯權限');
            return;
          }
          window.open(expenseUrl, '_blank');
          break;
        case 'prepayment':
          // 在新視窗中開啟預支記錄編輯頁面
          // 根據使用者權限決定跳轉到哪個路由
          let prepaymentUrl;
          if (this.ability.can('update', 'prepayments')) {
            // 管理員權限：跳轉到一般預支編輯頁面
            prepaymentUrl = this.$router.resolve({ name: 'advance-payment-edit', params: { id } }).href;
          } else if (this.ability.can('update', 'user_prepayments')) {
            // 員工權限：跳轉到員工預支編輯頁面
            prepaymentUrl = this.$router.resolve({ name: 'worker-advance-payment-edit', params: { id } }).href;
          } else {
            console.warn('沒有預支記錄編輯權限');
            return;
          }
          window.open(prepaymentUrl, '_blank');
          break;
        case 'refund':
          // 在新視窗中開啟退款編輯頁面
          const refundUrl = this.$router.resolve({ name: 'refund-edit', params: { id } }).href;
          window.open(refundUrl, '_blank');
          break;
        default:
          console.warn('未知的記錄類型:', type);
      }
    },

    // 匯出此頁面
    exportCurrentPage() {
      if (this.isExporting) return;

      this.isExporting = true;
      this.exportRecords('current')
        .then(exportData => {
          this.downloadExcel(exportData, '銀行進支記錄_當前頁面');
        })
        .catch(error => {
          console.error('匯出當前頁面失敗:', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "匯出失敗",
              text: "匯出當前頁面失敗，請稍後再試",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.isExporting = false;
        });
    },

    // 匯出全部記錄
    exportAllRecords() {
      if (this.isExporting) return;

      this.isExporting = true;
      this.exportRecords('all')
        .then(exportData => {
          this.downloadExcel(exportData, '銀行進支記錄_全部記錄');
        })
        .catch(error => {
          console.error('匯出全部記錄失敗:', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "匯出失敗",
              text: "匯出全部記錄失敗，請稍後再試",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.isExporting = false;
        });
    },

    // 下載 Excel 文件
    downloadExcel(data, filename) {
      if (!data || data.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "匯出失敗",
            text: "沒有數據可以匯出",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // 計算實際記錄數量（排除空白分隔行）
      const actualRecordCount = data.filter(row => {
        // 檢查是否為空白分隔行（所有欄位都為空字串）
        return !(row['日期'] === '' &&
                row['進支詳情'] === '' &&
                row['進支方式'] === '' &&
                row['存入'] === '' &&
                row['支出'] === '' &&
                row['儲蓄/往來'] === '' &&
                row['備注'] === '');
      }).length;

      // 匯出為 Excel (使用 ExcelJS 支援完整樣式)
      import("@/utils/excelExport").then(async (excelExport) => {
        try {
          // 使用專業級的銀行記錄匯出函數
          const success = await excelExport.export_bank_records_to_excel_styled({
            title: '銀行進支記錄',
            data: data,
            filename: filename,
            bookType: 'xlsx'
          });

          if (success) {
            // 顯示成功訊息，使用實際記錄數量
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "匯出成功",
                text: `已成功匯出 ${actualRecordCount} 筆記錄，檔案包含完整的格式化樣式`,
                icon: "CheckCircleIcon",
                variant: "success",
              },
            });
          } else {
            throw new Error('匯出過程中發生錯誤');
          }
        } catch (error) {
          console.error('Excel 匯出失敗:', error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "匯出失敗",
              text: "Excel 匯出過程中發生錯誤，請稍後再試",
              icon: "XCircleIcon",
              variant: "danger",
            },
          });
        }
      }).catch((error) => {
        console.error('匯出失敗:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "匯出失敗",
            text: "Excel 匯出功能載入失敗",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    },
  },
  setup() {
    const BANK_RECORDS_APP_STORE_MODULE_NAME = "bankRecords";

    // 註冊 store module
    if (!store.hasModule(BANK_RECORDS_APP_STORE_MODULE_NAME))
      store.registerModule(BANK_RECORDS_APP_STORE_MODULE_NAME, bankRecordsStoreModule);

    // 在組件卸載時取消註冊
    onUnmounted(() => {
      if (store.hasModule(BANK_RECORDS_APP_STORE_MODULE_NAME))
        store.unregisterModule(BANK_RECORDS_APP_STORE_MODULE_NAME);
    });

    const {
      fetchBankRecords,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      bankRecordsList,
      refBankRecordsListTable,
      refetchData,
      ability,
      filter_payment_method_id,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount,
      filter_is_savings,
      filter_transaction_type,
      paymentMethodOptions,
      exportRecords,
      allExpandedItems,
    } = useBankRecordsList();

    return {
      fetchBankRecords,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      bankRecordsList,
      refBankRecordsListTable,
      refetchData,
      ability,
      filter_payment_method_id,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount,
      filter_is_savings,
      filter_transaction_type,
      paymentMethodOptions,
      exportRecords,
      allExpandedItems,
    };
  },
};
</script>
    
<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// Re-Print 相關樣式
.badge-warning {
  background-color: #ffc107;
  color: #212529;
  font-size: 0.75em;
  padding: 0.25em 0.5em;
  border-radius: 0.25rem;
}

.text-warning {
  color: #856404 !important;
}

// 支付項目詳情樣式
// 銀行進支記錄表格樣式
.bank-records-table {
  .transaction-detail {
    .detail-text {
      font-weight: 500;
      color: #495057;

      // 可點擊編號樣式
      .record-link {
        color: #007bff !important;
        text-decoration: none;
        font-weight: 600;
        border-bottom: 1px dotted #007bff;
        transition: all 0.2s ease;

        &:hover {
          color: #0056b3 !important;
          border-bottom-style: solid;
          text-decoration: none;
        }

        &:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
          border-radius: 2px;
        }
      }
    }
  }

  .payment-method {
    font-size: 0.9rem;
    color: #6c757d;
    line-height: 1.4;

    .text-muted {
      font-size: 0.8rem;
      color: #868e96 !important;
    }

    .small {
      font-size: 0.8rem;
    }
  }

  .savings-switch {
    .switch-label {
      font-size: 0.85rem;
      margin-left: 0.5rem;
      color: #495057;
    }
  }

  .remark-field {
    .remark-input {
      border: 1px solid #e3e6f0;
      border-radius: 0.25rem;

      &:focus {
        border-color: #80bdff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }
  }

  // 金額欄位樣式
  .text-success {
    color: #28a745 !important;
  }

  .text-danger {
    color: #dc3545 !important;
  }

  // 表格行樣式
  tbody tr {
    &:hover {
      background-color: #f8f9fa;
    }
  }

  // 表格標題樣式
  thead th {
    background-color: #f8f9fc;
    border-bottom: 2px solid #e3e6f0;
    font-weight: 600;
    color: #5a5c69;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

// 保留原有樣式
.payment-item {
  border: 1px solid #e3e6f0;
  border-radius: 0.35rem;
  background-color: #f8f9fc;

  &:hover {
    background-color: #f1f3f4;
  }
}

.payment-item-description {
  font-weight: 600;
  color: #5a5c69;
}

.payment-item-meta {
  font-size: 0.8rem;
  color: #858796;
}

.payment-amount {
  font-weight: 600;
  font-size: 1rem;
}
</style>
